import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

const Contratos = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);

  const contratosData = [
    { id: 1, numero: 'CT-2024-001', cliente: 'YPF S.A.', tipo: 'Marco', descripcion: 'Servicios de Workover', inicio: '01/01/2024', fin: '31/12/2025', monto: 45000000, ejecutado: 38000000, estado: 'Vigente', version: 3 },
    { id: 2, numero: 'CT-2024-008', cliente: 'Pan American', tipo: 'Obra', descripcion: 'Perforación NW-012', inicio: '15/06/2025', fin: '15/06/2026', monto: 6500000, ejecutado: 1300000, estado: 'Vigente', version: 1 },
    { id: 3, numero: 'CT-2024-015', cliente: 'Tecpetrol', tipo: 'Servicio', descripcion: 'Mantenimiento Preventivo', inicio: '01/01/2025', fin: '15/02/2026', monto: 8000000, ejecutado: 7200000, estado: 'Por Vencer', version: 2 },
    { id: 4, numero: 'CT-2023-022', cliente: 'Vista Oil', tipo: 'Marco', descripcion: 'Servicios Integrales', inicio: '01/07/2023', fin: '30/06/2024', monto: 25000000, ejecutado: 25000000, estado: 'Finalizado', version: 4 },
  ];

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'Vigente': return 'success';
      case 'Por Vencer': return 'warning';
      case 'Vencido': return 'error';
      case 'Finalizado': return 'default';
      default: return 'primary';
    }
  };

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Contratos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de contratos con versionado
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
          Nuevo Contrato
        </Button>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="success.main">28</Typography>
              <Typography variant="body2" color="text.secondary">Vigentes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">4</Typography>
              <Typography variant="body2" color="text.secondary">Por Vencer</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="primary">$125M</Typography>
              <Typography variant="body2" color="text.secondary">Monto Total</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="info.main">15</Typography>
              <Typography variant="body2" color="text.secondary">Clientes</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabla */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Contrato</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Vigencia</TableCell>
                  <TableCell>Ejecución</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contratosData.map((row) => {
                  const porcentaje = Math.round((row.ejecutado / row.monto) * 100);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{row.numero}</Typography>
                        <Typography variant="caption" color="text.secondary">v{row.version}</Typography>
                      </TableCell>
                      <TableCell>{row.cliente}</TableCell>
                      <TableCell>
                        <Chip label={row.tipo} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{row.inicio} - {row.fin}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(porcentaje, 100)}
                            sx={{ flex: 1, height: 6, borderRadius: 3 }}
                          />
                          <Typography variant="caption">{porcentaje}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {row.estado === 'Por Vencer' && <WarningIcon fontSize="small" color="warning" sx={{ mr: 0.5 }} />}
                        <Chip label={row.estado} size="small" color={getEstadoColor(row.estado)} />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small"><ViewIcon fontSize="small" /></IconButton>
                        <IconButton size="small"><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small"><HistoryIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nuevo Contrato</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Cliente</InputLabel>
                <Select label="Cliente">
                  <MenuItem value="1">YPF S.A.</MenuItem>
                  <MenuItem value="2">Pan American Energy</MenuItem>
                  <MenuItem value="3">Tecpetrol</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Contrato</InputLabel>
                <Select label="Tipo de Contrato">
                  <MenuItem value="marco">Marco</MenuItem>
                  <MenuItem value="obra">Por Obra</MenuItem>
                  <MenuItem value="servicio">Servicio</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha Inicio" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha Fin" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Monto Total" type="number" />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Moneda</InputLabel>
                <Select label="Moneda">
                  <MenuItem value="ARS">Pesos (ARS)</MenuItem>
                  <MenuItem value="USD">Dólares (USD)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                Adjuntar Contrato (PDF)
                <input type="file" hidden />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>Crear Contrato</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contratos;
